<template>
    <TprmModal
        :additional-class-name="className"
        :open="open"
        :show-close-icon="allowClose"
        @close="$emit('close')"
    >
        <template v-if="$slots.header" #header-title-container>
            <slot name="header" />
        </template>
        <slot>
            <div class="ActionStatusModal__content">
                <div
                    v-if="type==='success'"
                    class="ActionStatusModal__successIcon"
                >
                    <FontAwesomeIcon :icon="faCheckCircle" />
                </div>
                <div
                    v-else-if="type==='error'"
                    class="ActionStatusModal__errorIcon"
                >
                    <FontAwesomeIcon :icon="faExclamationTriangle" />
                </div>
                <div class="ActionStatusModal__message">
                    {{ message }}
                </div>
            </div>
        </slot>
        <template v-if="$slots.footer" #footer>
            <slot name="footer" />
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    // PRODUCT-26110 QA fixes4: declares the `close` event this component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'close' but it is
    // neither declared in the emits option nor as an 'onClose' prop" every time it fires.
    @Component({
        components: {
            FontAwesomeIcon,
            TprmModal,
        },
        emits: [ 'close' ],
    })
    export default class ActionStatusModal extends Vue {
        @Prop({ type: Boolean, default: false })
        private allowClose!: boolean;

        @Prop({ type: Boolean, default: false })
        private open!: boolean;

        @Prop({ type: String, default: 'success' })
        private type!: 'success' | 'error';

        @Prop({ type: String, default: '' })
        private message!: string;

        // PRODUCT-26110 QA fixes5: procedure-injected attrs across modal chains --
        // SendTestEmailProcedure passes `additional-class-name="Send"` to this component
        // but it was previously undeclared, so it (a) silently never reached the DOM (the
        // template hardcoded its own `className` onto TprmModal, ignoring the caller's
        // value) and (b) fell through as an unconsumed attr all the way to Modal.vue's
        // `<Teleport>` root, where Vue warned "Extraneous non-props attributes". Declared
        // and merged into `className` below, matching the same pattern LoadingModal.vue
        // and CollateralMappingModal.vue already use for their own `additionalClassName`.
        @Prop({ type: String, default: '' })
        private additionalClassName!: string;

        private faCheckCircle = faCheckCircle;
        private faExclamationTriangle = faExclamationTriangle;

        private get hideHeader(): boolean {
            return !this.$slots.header && !this.allowClose;
        }

        private get className(): string {
            const base = `ActionStatusModal${this.hideHeader ? ' ActionStatusModal--noHeader' : ''}`;
            return this.additionalClassName ? `${base} ${this.additionalClassName}` : base;
        }
    }
</script>
